import { Slider } from '@mui/material';
import { formatNumber } from 'util/Funcs';
import { useEffect, useState } from 'react';

interface StepDefinition {
    max: number;
    step: number;
}

interface RangeFilterProps {
    Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
    title?: string;
    description?: string;
    unit: string;
    value?: [number, number];
    limit: [number, number];
    steps: StepDefinition[];
    onChanged: (value: [number, number]) => void;
    isShownClearButton?: boolean;
    error?: string;
    classNames?: string;
    showBorderAndText?: boolean;
}

const generateStepValues = (steps: StepDefinition[], min: number): number[] => {
    let values: number[] = [min];
    let current = min;
    for (const range of steps) {
        while (current + range.step <= range.max) {
            current += range.step;
            values.push(current);
        }
    }
    return values;
};

const mapSliderToValue = (sliderIndex: number, stepValues: number[]): number => stepValues[sliderIndex];

const mapValueToSlider = (actualValue: number, stepValues: number[]): number => {
    const index = stepValues.findIndex((val) => val >= actualValue);
    return index === -1 ? stepValues.length - 1 : index;
};

export default function RangeFilter({
    Icon,
    title,
    description,
    unit,
    value,
    limit,
    steps,
    onChanged,
    isShownClearButton = false,
    error,
    classNames,
    showBorderAndText = true,
}: RangeFilterProps) {
    const [min, max] = limit;

    const initialValue: [number, number] = value && typeof value[0] === 'number' && typeof value[1] === 'number' ? value : [min, min];

    const STEP_VALUES = generateStepValues(steps, min);

    const [rawRange, setRawRange] = useState<[number, number]>(initialValue);

    const sliderRange: [number, number] = [mapValueToSlider(rawRange[0], STEP_VALUES), mapValueToSlider(rawRange[1], STEP_VALUES)];

    useEffect(() => {
        setRawRange(value && typeof value[0] === 'number' && typeof value[1] === 'number' ? value : [min, min]);
    }, [value, min]);

    const handleSliderChange = (_: Event, newValue: number | number[]) => {
        if (Array.isArray(newValue)) {
            const newSliderRange: [number, number] = [newValue[0], newValue[1]];
            const actualMin = mapSliderToValue(newSliderRange[0], STEP_VALUES);
            const actualMax = mapSliderToValue(newSliderRange[1], STEP_VALUES);
            setRawRange([actualMin, actualMax]);
            onChanged([actualMin, actualMax]);
        }
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
        const cleaned = e.target.value.replace(/\./g, '');
        const val = Number(cleaned) || 0;
        setRawRange((prev) => {
            const newValue: [number, number] = idx === 0 ? [val, prev[1]] : [prev[0], val];
            onChanged(newValue);
            return newValue;
        });
    };

    const handleClear = () => {
        setRawRange([min, min]);
        onChanged([min, min]);
    };

    const isFilterAdded = rawRange[0] !== min || rawRange[1] !== min;

    return (
        <div
            className={`${showBorderAndText ? 'p-5 border rounded-lg w-full' : ''} ${
                isFilterAdded && isShownClearButton ? 'border-my-blue border-2' : 'border-gray-300'
            } font-poppins ${classNames}`}
        >
            <div className="flex gap-2">
                {Icon && <Icon className="h-6 w-6" />}
                {title && <p className="font-medium text-base text-my-black/80">{title}</p>}
            </div>
            {description && <h4 className="mt-3 text-my-black/50 font-poppins font-normal text-[12px]">{description}</h4>}
            <div className="pr-2">
                <Slider
                    value={sliderRange}
                    onChange={handleSliderChange}
                    valueLabelDisplay="auto"
                    min={0}
                    max={STEP_VALUES.length - 1}
                    step={1}
                    valueLabelFormat={(val) => `${formatNumber(mapSliderToValue(val, STEP_VALUES))}${unit}`}
                />
            </div>
            <div className="flex default-sm:flex-row flex-col gap-4 my-2">
                <RangeInput label="Min." currency={unit} value={rawRange[0]} onChange={(e) => onInputChange(e, 0)} />
                <RangeInput label="Max." currency={unit} value={rawRange[1]} onChange={(e) => onInputChange(e, 1)} />
            </div>
            {isFilterAdded && isShownClearButton && (
                <button className="absolute top-2 right-3 text-my-blue" onClick={handleClear}>
                    Clear
                </button>
            )}
            {error && <p className="text-red-500 text-sm !mt-2">{error}</p>}
        </div>
    );
}

interface RangeInputProps {
    label: string;
    value: number;
    currency?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const RangeInput = ({ label, value, currency, onChange }: RangeInputProps) => {
    return (
        <div className="relative flex items-center w-full pb-2">
            <span className="absolute left-1.5 font-medium text-base">{label}</span>
            <input
                type="text"
                className="w-full px-12 py-2.5 text-right border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formatNumber(value)}
                onChange={onChange}
            />
            <span className="absolute right-4 text-base font-medium">{currency}</span>
        </div>
    );
};
